import { Typography } from '@euler/app/components/typography';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useSwitchAccount } from '@euler/app/flows/auth/hooks';
import { Icon } from '@euler/components/Icomoon';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AuthenticatorNextActionApp } from '@euler/model/auth';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert } from '@euler/utils';
import { useNavigation } from '@react-navigation/native';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from 'styled-components/native';

function getDisplayInfo(app: AuthenticatorNextActionApp): {
  title: string;
  subTitle?: string;
} {
  if (app.orgName && app.storeName) {
    return { title: app.storeName, subTitle: app.orgName };
  }
  const raw = app.appTitle ?? app.appName;
  const dashIndex = raw.indexOf('-');
  if (dashIndex !== -1) {
    return {
      subTitle: raw.slice(0, dashIndex).trim(),
      title: raw.slice(dashIndex + 1).trim(),
    };
  }
  return { title: raw };
}

type AppSwitchItemProps = {
  item: AuthenticatorNextActionApp;
  isCurrent: boolean;
  onPress: () => void;
};

const AppSwitchItem = memo(
  ({ item, isCurrent, onPress }: AppSwitchItemProps) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const { title, subTitle } = getDisplayInfo(item);

    return (
      <TouchableOpacity
        onPress={onPress}
        disabled={isCurrent}
        css={`
          padding: ${rpx(16)}px ${rpx(20)}px;
          margin-bottom: ${rpx(12)}px;
          background-color: ${isCurrent
            ? theme.link
            : theme.page.card.backgroundColor};
          border-radius: ${rpx(8)}px;
          border-width: 2px;
          border-color: ${isCurrent ? theme.link : 'transparent'};
          flex-direction: row;
          align-items: center;
          opacity: 1;
        `}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          {subTitle ? (
            <Typography.Text
              light
              size={rpx(12)}
              color={
                isCurrent
                  ? 'rgba(255,255,255,0.7)'
                  : theme.page.header.subTitleColor
              }
              style={{ marginBottom: rpx(4) }}
            >
              {subTitle}
            </Typography.Text>
          ) : null}
          <Typography.Text
            medium
            size={rpx(16)}
            color={isCurrent ? '#fff' : theme.page.header.titleColor}
          >
            {title}
          </Typography.Text>
        </View>
        {isCurrent && (
          <Icon name="checkmark-circle" color="#fff" size={rpx(22)} />
        )}
      </TouchableOpacity>
    );
  },
);

export const AppSwitchScreen = wrapNavigatorScreen(
  () => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const { authService } = useServiceFactory();
    const currentUser = useCurrentUser();
    const switchAccount = useSwitchAccount();
    const navigation = useNavigation();
    const loading = useAppLoading();
    const confirm = useConfirm();

    const [apps, setApps] = useState<AuthenticatorNextActionApp[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const currentAppId = currentUser?.app.id;

    useEffect(() => {
      let cancelled = false;
      setIsLoading(true);
      setError(null);
      authService
        .getApps()
        .then(result => {
          if (!cancelled) {
            setApps(result);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError('获取门店列表失败，请稍后重试');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }, [authService]);

    const handleSwitch = useCallback(
      async (appId: IdType) => {
        const confirmed = await confirm({
          title: '切换门店',
          message: '确定要切换到该门店吗？',
        });
        if (!confirmed) return;

        try {
          loading.show();
          await switchAccount(appId);
          navigation.goBack();
        } catch {
          void showAlert('切换门店失败，请稍后重试');
        } finally {
          loading.hide();
        }
      },
      [confirm, switchAccount, navigation, loading],
    );

    if (isLoading) {
      return (
        <View
          css={`
            flex: 1;
            justify-content: center;
            align-items: center;
            background-color: #fff;
          `}
        >
          <ActivityIndicator size="large" color={theme.link} />
        </View>
      );
    }

    if (error) {
      return (
        <View
          css={`
            flex: 1;
            justify-content: center;
            align-items: center;
            background-color: #fff;
            padding: ${rpx(32)}px;
          `}
        >
          <Typography.Text size={rpx(14)} color={theme.label.color}>
            {error}
          </Typography.Text>
        </View>
      );
    }

    return (
      <View
        css={`
          flex: 1;
          background-color: #fff;
          padding: ${rpx(16)}px;
        `}
      >
        <FlatList
          data={apps}
          keyExtractor={item => String(item.id)}
          renderItem={({ item }) => (
            <AppSwitchItem
              item={item}
              isCurrent={currentAppId === item.id}
              onPress={() => void handleSwitch(item.id)}
            />
          )}
        />
      </View>
    );
  },
  { title: '切换门店' },
);
